import React, {useState, useEffect, useRef} from "react";
import {
    mdiAccount, mdiChefHat, mdiSilverwareForkKnife, mdiCashRegister,
    mdiAccountTie, mdiAccountHardHat, mdiTruckDelivery, mdiAccountSupervisor,
    mdiStar, mdiAccountStar, mdiSecurity, mdiBroom, mdiCoffee,
    mdiGlassCocktail, mdiStorefront, mdiClipboardAccount, mdiAccountGroup,
    mdiCrown, mdiWrench, mdiShieldAccount, mdiFaceMan, mdiFaceWoman,
    mdiAccountCog, mdiMoped
} from "@mdi/js";

/**
 * Employee color/icon presets — kept in sync with pos_clone (tablet) and
 * clicoo (API): `icon` stores the Material Design Icons (Pictogrammers)
 * glyph name, rendered via react-native-vector-icons/MaterialCommunityIcons
 * on the tablet and @mdi/js here, so the same name works unchanged in both.
 */
export const PRESET_COLORS = [
    '#4DB6AC', '#26A69A', '#009688', '#00897B', '#00796B',
    '#EF5350', '#EC407A', '#AB47BC', '#7E57C2', '#5C6BC0',
    '#42A5F5', '#29B6F6', '#26C6DA', '#66BB6A', '#9CCC65',
    '#D4E157', '#FFEE58', '#FFCA28', '#FFA726', '#FF7043',
    '#8D6E63', '#78909C', '#455A64', '#37474F', '#263238',
];

export const EMPLOYEE_ICONS = [
    {name: 'account', label: 'Par défaut', path: mdiAccount},
    {name: 'chef-hat', label: 'Chef cuisinier', path: mdiChefHat},
    {name: 'silverware-fork-knife', label: 'Service en salle', path: mdiSilverwareForkKnife},
    {name: 'cash-register', label: 'Caissier', path: mdiCashRegister},
    {name: 'account-tie', label: 'Manager', path: mdiAccountTie},
    {name: 'account-hard-hat', label: 'Technicien', path: mdiAccountHardHat},
    {name: 'truck-delivery', label: 'Livreur', path: mdiTruckDelivery},
    {name: 'moped', label: 'Livraison rapide', path: mdiMoped},
    {name: 'account-supervisor', label: 'Superviseur', path: mdiAccountSupervisor},
    {name: 'crown', label: 'Responsable', path: mdiCrown},
    {name: 'shield-account', label: 'Admin', path: mdiShieldAccount},
    {name: 'account-cog', label: 'Configuration', path: mdiAccountCog},
    {name: 'account-group', label: 'Équipe', path: mdiAccountGroup},
    {name: 'clipboard-account', label: 'Réception', path: mdiClipboardAccount},
    {name: 'storefront', label: 'Vente', path: mdiStorefront},
    {name: 'coffee', label: 'Barista', path: mdiCoffee},
    {name: 'glass-cocktail', label: 'Bar', path: mdiGlassCocktail},
    {name: 'security', label: 'Sécurité', path: mdiSecurity},
    {name: 'broom', label: 'Entretien', path: mdiBroom},
    {name: 'wrench', label: 'Maintenance', path: mdiWrench},
    {name: 'star', label: 'Étoile', path: mdiStar},
    {name: 'account-star', label: 'Employé du mois', path: mdiAccountStar},
    {name: 'face-man', label: 'Homme', path: mdiFaceMan},
    {name: 'face-woman', label: 'Femme', path: mdiFaceWoman},
];

const ICON_BY_NAME = EMPLOYEE_ICONS.reduce((acc, i) => {
    acc[i.name] = i;
    return acc;
}, {});

export const DEFAULT_EMPLOYEE_COLOR = PRESET_COLORS[0];
export const DEFAULT_EMPLOYEE_ICON = EMPLOYEE_ICONS[0].name;

export const MdiIcon = ({path, size = 20, color = 'currentColor', className}) => (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className}>
        <path fill={color} d={path}/>
    </svg>
);

/** Colored badge for an employee: renders its MDI icon, falling back to initials. */
export const EmployeeAvatar = ({employee, size = 40, className = ''}) => {
    const color = employee?.color || DEFAULT_EMPLOYEE_COLOR;
    const icon = employee?.icon && ICON_BY_NAME[employee.icon];
    const initials = (employee?.name || '??').slice(0, 2).toUpperCase();

    return (
        <div
            className={`rounded-xl flex-shrink-0 flex items-center justify-center text-white font-bold shadow-sm ${className}`}
            style={{backgroundColor: color, width: size, height: size}}
        >
            {icon ? (
                <MdiIcon path={icon.path} size={Math.round(size * 0.55)} color="#fff"/>
            ) : (
                <span style={{fontSize: Math.round(size * 0.35)}}>{initials}</span>
            )}
        </div>
    );
};

export const MiniColorPicker = ({value, onChange}) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div ref={ref} className="relative inline-block">
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="w-9 h-9 rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm hover:shadow-md transition-shadow"
                style={{backgroundColor: value || DEFAULT_EMPLOYEE_COLOR}}
            />
            {open && (
                <div className="absolute z-50 mt-2 left-0 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-3 w-52">
                    <div className="grid grid-cols-5 gap-1.5">
                        {PRESET_COLORS.map(c => (
                            <button
                                key={c}
                                type="button"
                                onClick={() => {
                                    onChange(c);
                                    setOpen(false);
                                }}
                                className={`w-8 h-8 rounded-md transition-transform hover:scale-110 ${c === value ? 'ring-2 ring-offset-1 ring-primary' : ''}`}
                                style={{backgroundColor: c}}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export const IconPicker = ({value, color, onChange}) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const current = ICON_BY_NAME[value] || EMPLOYEE_ICONS[0];

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div ref={ref} className="relative inline-block">
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="w-9 h-9 rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm hover:shadow-md transition-shadow flex items-center justify-center"
                style={{backgroundColor: color || DEFAULT_EMPLOYEE_COLOR}}
                title={current.label}
            >
                <MdiIcon path={current.path} size={18} color="#fff"/>
            </button>
            {open && (
                <div className="absolute z-50 mt-2 left-0 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-3 w-64">
                    <div className="grid grid-cols-6 gap-1.5">
                        {EMPLOYEE_ICONS.map(i => (
                            <button
                                key={i.name}
                                type="button"
                                title={i.label}
                                onClick={() => {
                                    onChange(i.name);
                                    setOpen(false);
                                }}
                                className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 ${
                                    i.name === value ? 'ring-2 ring-offset-1 ring-primary bg-gray-100 dark:bg-gray-700' : ''
                                }`}
                            >
                                <MdiIcon path={i.path} size={18} color="currentColor" className="text-gray-600 dark:text-gray-300"/>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
